import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL, api } from "../api.ts";
import { keys } from "../lib/query.ts";

async function unwrap<T>(promise: Promise<{ data: T | null; error: unknown }>): Promise<T> {
  const { data, error } = await promise;
  if (error !== null) throw (error as { value?: unknown }).value ?? error;
  if (data === null) throw new Error("the API returned no body");
  return data;
}

type Shop = ReturnType<typeof api.v1.franchises>["shop"];

export type ShopCatalogue = NonNullable<Awaited<ReturnType<Shop["products"]["get"]>>["data"]>;
export type ShopProductSummary = ShopCatalogue["products"][number];
export type ShopVariant = ShopProductSummary["variants"][number];
export type ShopProduct = NonNullable<
  Awaited<ReturnType<ReturnType<Shop["products"]>["get"]>>["data"]
>;

export function useShopCatalogue(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.shopCatalogue(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string }).shop.products.get()),
  });
}

export function useShopProduct(franchiseId: string | null, productSlug: string) {
  return useQuery({
    queryKey: keys.shopProduct(franchiseId ?? "", productSlug),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          .shop.products({ productSlug })
          .get(),
      ),
  });
}

type Admin = typeof api.v1.admin.shop.orders;

export type ShopOrder = NonNullable<Awaited<ReturnType<ReturnType<Shop["orders"]>["get"]>>["data"]>;
export type ShopOrderSummary = NonNullable<
  Awaited<ReturnType<Shop["orders"]["get"]>>["data"]
>["data"][number];
export type ShopOrderStatus =
  | "submitted"
  | "approved"
  | "partially_shipped"
  | "rejected"
  | "shipped"
  | "cancelled"
  | "delivered";

export interface DeliveryAddressInput {
  readonly name: string;
  readonly address_line1: string;
  readonly address_line2: string | null;
  readonly postcode: string;
  readonly city: string;
}

export interface OrderSubmission {
  readonly idempotencyKey: string;
  readonly lines: readonly { readonly variantId: string; readonly qty: number }[];
  readonly notes: string | null;
  readonly deliveryAddress: DeliveryAddressInput | null;
}

export function useSubmitOrder(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (order: OrderSubmission) =>
      unwrap(
        api.v1.franchises({ franchiseId }).shop.orders.post({
          idempotency_key: order.idempotencyKey,
          lines: order.lines.map((line) => ({ variant_id: line.variantId, qty: line.qty })),
          notes: order.notes,
          delivery_address: order.deliveryAddress,
        }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.shopOrders(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.shopCatalogue(franchiseId) });
    },
  });
}

export function useShopOrders(franchiseId: string | null) {
  return useInfiniteQuery({
    queryKey: keys.shopOrders(franchiseId ?? ""),
    enabled: franchiseId !== null,
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          .shop.orders.get({ query: pageParam === "" ? {} : { cursor: pageParam } }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

export function useShopOrder(franchiseId: string | null, orderId: string) {
  return useQuery({
    queryKey: keys.shopOrder(franchiseId ?? "", orderId),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          .shop.orders({ orderId })
          .get(),
      ),
  });
}

export function useCancelOrder(franchiseId: string, orderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(api.v1.franchises({ franchiseId }).shop.orders({ orderId }).cancel.post()),
    onSuccess: (order) => client.setQueryData(keys.shopOrder(franchiseId, orderId), order),
    onSettled: () => void client.invalidateQueries({ queryKey: keys.shopOrders(franchiseId) }),
  });
}

export function useConfirmReceived(franchiseId: string, orderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(api.v1.franchises({ franchiseId }).shop.orders({ orderId }).received.post()),
    onSuccess: (order) => client.setQueryData(keys.shopOrder(franchiseId, orderId), order),
    onSettled: () => void client.invalidateQueries({ queryKey: keys.shopOrders(franchiseId) }),
  });
}

export function useHqShopOrders(status: ShopOrderStatus | null) {
  return useInfiniteQuery({
    queryKey: keys.hqShopOrders(status ?? "all"),
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      unwrap(
        api.v1.admin.shop.orders.get({
          query: {
            ...(status === null ? {} : { status }),
            ...(pageParam === "" ? {} : { cursor: pageParam }),
          },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

export function useHqShopOrder(orderId: string) {
  return useQuery({
    queryKey: keys.hqShopOrder(orderId),
    queryFn: () => unwrap(api.v1.admin.shop.orders({ orderId }).get()),
  });
}

function useHqDecision<T>(
  orderId: string,
  send: (order: ReturnType<Admin>, body: T) => Promise<{ data: ShopOrder | null; error: unknown }>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: T) => unwrap(send(api.v1.admin.shop.orders({ orderId }), body)),
    onSuccess: (order) => client.setQueryData(keys.hqShopOrder(orderId), order),
    onSettled: () => void client.invalidateQueries({ queryKey: keys.hqShopOrdersAll() }),
  });
}

export function useApproveOrder(orderId: string) {
  return useHqDecision(
    orderId,
    (order, body: { lines: { id: string; qty: number }[]; note: string | null }) =>
      order.approve.post(body),
  );
}

export function useRejectOrder(orderId: string) {
  return useHqDecision(orderId, (order, body: { note: string }) => order.reject.post(body));
}

export function useShipOrder(orderId: string) {
  return useHqDecision(
    orderId,
    (order, body: { lines: { id: string; qty: number }[]; tracking_number: string | null }) =>
      order.ship.post(body),
  );
}

export function useHqCancelOrder(orderId: string) {
  return useHqDecision(orderId, (order, body: { note: string }) => order.cancel.post(body));
}

export function useCloseOrder(orderId: string) {
  return useHqDecision(orderId, (order, body: { note: string }) => order.close.post(body));
}

export function ownerInvoiceUrl(franchiseId: string, invoiceId: string): string {
  return `${API_URL}/v1/franchises/${franchiseId}/shop/invoices/${invoiceId}`;
}

export function hqInvoiceUrl(invoiceId: string): string {
  return `${API_URL}/v1/admin/shop/invoices/${invoiceId}`;
}

export type HqNotificationFeed = NonNullable<
  Awaited<ReturnType<typeof api.v1.admin.notifications.get>>["data"]
>;

export function useHqNotifications(enabled: boolean) {
  return useQuery({
    queryKey: keys.hqNotificationBell(),
    enabled,
    queryFn: () => unwrap(api.v1.admin.notifications.get({ query: { limit: 5 } })),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useMarkHqNotificationsRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: readonly string[]) =>
      unwrap(api.v1.admin.notifications.read.post({ ids: [...ids] })),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.hqNotifications() });
      void client.invalidateQueries({ queryKey: keys.hqShopOrdersAll() });
    },
  });
}

export type HqNotificationKind =
  | "shop_order_placed"
  | "shop_order_cancelled"
  | "shop_order_delivered";

export function useHqNotificationHistory(kind: HqNotificationKind | null, unreadOnly: boolean) {
  return useInfiniteQuery({
    queryKey: keys.hqNotificationHistory(kind ?? "", unreadOnly),
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      unwrap(
        api.v1.admin.notifications.get({
          query: {
            limit: 30,
            ...(pageParam === "" ? {} : { cursor: pageParam }),
            ...(kind === null ? {} : { kind }),
            ...(unreadOnly ? { unread: true } : {}),
          },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

export function useOrderEmails(orderId: string) {
  return useQuery({
    queryKey: keys.orderEmails(orderId),
    queryFn: () => unwrap(api.v1.admin.shop.orders({ orderId }).emails.get()),
    refetchInterval: 15_000,
  });
}

export function useResendEmail(orderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (emailId: string) => unwrap(api.v1.admin.shop.emails({ emailId }).resend.post()),
    onSettled: () => void client.invalidateQueries({ queryKey: keys.orderEmails(orderId) }),
  });
}

export function shopExportUrl(kind: "orders" | "invoices", from: string, to: string): string {
  return `${API_URL}/v1/admin/shop/exports/${kind}?from=${from}&to=${to}`;
}

export type ShopAnalytics = NonNullable<
  Awaited<ReturnType<typeof api.v1.admin.shop.analytics.get>>["data"]
>;

export function useShopAnalytics(
  filter: { readonly from: string; readonly to: string; readonly country: string | null },
  enabled: boolean,
) {
  return useQuery({
    queryKey: keys.shopAnalytics(filter.from, filter.to, filter.country ?? ""),
    enabled,
    queryFn: () =>
      unwrap(
        api.v1.admin.shop.analytics.get({
          query: {
            from: filter.from,
            to: filter.to,
            ...(filter.country === null ? {} : { country: filter.country }),
          },
        }),
      ),
  });
}
